/**
 * Selection summary panel.
 * Desktop/tablet (md+): draggable floating panel, clamped to the viewport.
 * Mobile (<md): non-draggable bottom sheet with a sticky header.
 */

import { useEffect, useRef, useState, useCallback } from "react";

import { useLanguageContext } from "../hooks/useLanguage";
import { useIsMobile } from "../hooks/useMediaQuery";
import type { GeoJSONFeature, RegionConfig } from "../types";
import { lineLengthMeters } from "../utils/geometry";

interface FloatingSelectionSummaryProps {
  features: GeoJSONFeature[];
  config: RegionConfig;
  selectionType: "polygon" | "viewport";
  selectedMunicipality: string;
  onClose: () => void;
  onExportPdf: () => void;
}

const HEADER_SAFE_TOP = 72; // keep the panel below the app header when dragging

export function FloatingSelectionSummary({
  features,
  config,
  selectionType,
  selectedMunicipality,
  onClose,
  onExportPdf,
}: FloatingSelectionSummaryProps) {
  const { t } = useLanguageContext();
  const isMobile = useIsMobile();
  const panelRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState(() => ({
    x: typeof window !== "undefined" ? Math.max(8, window.innerWidth - 460) : 100,
    y: 120,
  }));
  const [dragging, setDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  const titleKey =
    selectionType === "polygon" ? "polygon_selection_title" : "viewport_selection_title";

  // Drag handlers (desktop/tablet only)
  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (isMobile) return;
      e.preventDefault();
      setDragging(true);
      dragOffset.current = {
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      };
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
    },
    [isMobile, position],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragging) return;
      const newX = e.clientX - dragOffset.current.x;
      const newY = e.clientY - dragOffset.current.y;
      // Clamp to viewport using the panel's actual rendered size
      const panelWidth = panelRef.current?.offsetWidth ?? 440;
      const panelHeight = panelRef.current?.offsetHeight ?? 0;
      const maxX = Math.max(8, window.innerWidth - panelWidth - 8);
      const maxY = Math.max(HEADER_SAFE_TOP, window.innerHeight - Math.min(panelHeight, 80));
      const clampedX = Math.max(8, Math.min(newX, maxX));
      const clampedY = Math.max(HEADER_SAFE_TOP, Math.min(newY, maxY));
      setPosition({ x: clampedX, y: clampedY });
    },
    [dragging],
  );

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    setDragging(false);
    (e.target as HTMLElement).releasePointerCapture(e.pointerId);
  }, []);

  // Escape closes
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  // Keep a desktop panel on-screen if the window is resized narrower.
  useEffect(() => {
    if (isMobile) return;
    const handleResize = () => {
      setPosition((prev) => {
        const panelWidth = panelRef.current?.offsetWidth ?? 440;
        const maxX = Math.max(8, window.innerWidth - panelWidth - 8);
        const maxY = Math.max(HEADER_SAFE_TOP, window.innerHeight - 80);
        return {
          x: Math.min(prev.x, maxX),
          y: Math.min(prev.y, maxY),
        };
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isMobile]);

  const containerClassName = isMobile
    ? "fixed inset-x-0 bottom-0 z-40 mx-auto flex max-h-[65dvh] w-full max-w-[calc(100vw-0.5rem)] flex-col overflow-hidden rounded-t-2xl border border-flima-spruce/15 bg-white shadow-float"
    : "fixed z-40 flex max-h-[70vh] w-[min(28rem,calc(100vw-1rem))] flex-col overflow-hidden rounded-lg border border-flima-spruce/15 bg-white shadow-float";

  const rows = features.map((feature, index) => {
    const props = feature.properties as Record<string, unknown>;
    const fundort = (props.FUNDORT as string) || "—";
    const speciesId = props.species_id as string;
    const speciesName =
      speciesId === "both"
        ? t("both_species")
        : speciesId
          ? config.species.find((s) => s.id === speciesId)?.name || speciesId
          : t("standalone_measure");
    const activeMeasures = Array.isArray(props.measures)
      ? (props.measures as string[])
      : [];
    const lengthM = Math.round(lineLengthMeters(feature.geometry));
    return { activeMeasures, fundort, index, lengthM, speciesName };
  });

  const renderMeasureChips = (activeMeasures: string[]) => (
    <div className="flex flex-wrap gap-1">
      {activeMeasures.length > 0 ? (
        activeMeasures.map((id) => {
          const mcfg = config.measures.find((m) => m.id === id);
          return (
            <span
              key={id}
              className="inline-flex max-w-full items-center gap-1 rounded px-1.5 py-0.5 text-xs"
              style={{
                backgroundColor: `${mcfg?.color || "#999"}20`,
                color: mcfg?.color || "#999",
              }}
            >
              <span
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ backgroundColor: mcfg?.color || "#999" }}
                aria-hidden="true"
              />
              <span className="min-w-0 break-words">{mcfg?.label || id}</span>
            </span>
          );
        })
      ) : (
        <span className="text-xs text-flima-slate/45">—</span>
      )}
    </div>
  );

  return (
    <div
      ref={panelRef}
      className={containerClassName}
      style={isMobile ? undefined : { left: position.x, top: position.y }}
      role="dialog"
      aria-label={t(titleKey)}
    >
      {/* Drag handle / header — sticky so close/export stay reachable while scrolling */}
      <div
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        className={`flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-flima-spruce/10 bg-flima-spruce px-4 py-2.5 text-white ${
          isMobile ? "cursor-default" : "cursor-grab active:cursor-grabbing"
        }`}
        style={isMobile ? undefined : { touchAction: "none" }}
        aria-label={isMobile ? undefined : t("drag_summary_panel")}
      >
        <div className="min-w-0">
          <h3 className="truncate font-display text-sm font-semibold">
            {t(titleKey)}
          </h3>
          <p className="truncate font-utility text-xs text-white/60">
            {features.length} {t("area_feature_count")} · {selectedMunicipality}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-1.5">
          <button
            type="button"
            onClick={onExportPdf}
            className="flex min-h-9 items-center gap-1 rounded-md border border-white/20 bg-white/5 px-2.5 py-1 text-xs text-white/85 transition-colors hover:bg-white/15 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white motion-reduce:transition-none"
            aria-label={t("export_pdf")}
            title={t("export_pdf")}
          >
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <path d="M14 2v6h6M12 18v-6M9 15h6" />
            </svg>
            {t("export_pdf")}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-md text-xl leading-none text-white/70 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white motion-reduce:transition-none"
            aria-label={t("area_clear")}
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {features.length === 0 ? (
          <p className="px-4 py-6 text-sm text-flima-slate/60">{t("area_empty")}</p>
        ) : (
          <>
            <div className="space-y-2 p-3 md:hidden">
              {rows.map((row) => (
                <article
                  key={row.index}
                  className="rounded-lg border border-flima-spruce/10 bg-flima-mist/35 p-3 text-sm text-flima-slate"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="break-words font-semibold text-flima-spruce">{row.fundort}</p>
                      <p className="mt-0.5 break-words text-xs text-flima-slate/60">
                        {row.speciesName}
                      </p>
                    </div>
                    <p className="shrink-0 font-utility text-xs text-flima-slate">
                      {row.lengthM} m
                    </p>
                  </div>
                  <div className="mt-2">{renderMeasureChips(row.activeMeasures)}</div>
                </article>
              ))}
            </div>
            <table className="hidden w-full text-left text-sm md:table">
            <thead className="sticky top-0 z-10 bg-flima-mist/80 backdrop-blur">
              <tr>
                <th className="px-3 py-2 font-utility text-[0.65rem] uppercase tracking-wider text-flima-slate/70">
                  {t("area_col_location")}
                </th>
                <th className="px-3 py-2 font-utility text-[0.65rem] uppercase tracking-wider text-flima-slate/70">
                  {t("area_col_species")}
                </th>
                <th className="px-3 py-2 font-utility text-[0.65rem] uppercase tracking-wider text-flima-slate/70">
                  {t("area_col_measures")}
                </th>
                <th className="px-3 py-2 text-right font-utility text-[0.65rem] uppercase tracking-wider text-flima-slate/70">
                  {t("area_col_length")}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                return (
                  <tr
                    key={row.index}
                    className="border-b border-flima-spruce/5 hover:bg-flima-mist/50"
                  >
                    <td className="px-3 py-2 text-flima-slate">{row.fundort}</td>
                    <td className="px-3 py-2 text-flima-slate">{row.speciesName}</td>
                    <td className="px-3 py-2 text-flima-slate">
                      {renderMeasureChips(row.activeMeasures)}
                    </td>
                    <td className="px-3 py-2 text-right font-utility text-flima-slate">
                      {row.lengthM} m
                    </td>
                  </tr>
                );
              })}
            </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}
